import React, { Dispatch, ReactElement, SetStateAction, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorDto } from 'lib_api/lib/api/gen';

import { UnauthorizedPage } from 'components/Error';
import UnauthentifiedHeader from 'components/Header/UnauthentifiedHeader';

import OpenIdConnectIDP from './OpenIdConnectIDP';

interface Props {
  setToken: Dispatch<SetStateAction<string | undefined>>;
  setRefreshToken: Dispatch<SetStateAction<string | undefined>>;
  children?: React.ReactElement;
  shouldConnect: boolean;
}

/**
 * When authentication is need, use the proper IDP.
 */
export default function WithIDP({
  shouldConnect,
  setToken,
  setRefreshToken,
  children,
}: Props): ReactElement {
  const [authError, setAuthError] = useState<ErrorDto>();
  const navigate = useNavigate();

  // Authentication already failed once
  if (authError) {
    return (
      <>
        <UnauthentifiedHeader className={'Header'} />
        <UnauthorizedPage error={authError} />
      </>
    );
  }

  // Need to authenticate
  if (shouldConnect) {
    return (
      <OpenIdConnectIDP
        setToken={setToken}
        setRefreshToken={setRefreshToken}
        setAuthError={setAuthError}
      />
    );
  }

  // Already authenticated
  // Keycloak automatically appends session state and code to the url, not allowing any refresh
  const code = new URLSearchParams(globalThis.location.search).get('code');
  const sessionState = new URLSearchParams(globalThis.location.search).get(
    'session_state',
  );

  // Remove both code and session_state from the url
  if (code || sessionState) {
    globalThis.history.replaceState(
      null,
      '',
      globalThis.location.origin + globalThis.location.pathname,
    );
  }
  const targetPath = localStorage.getItem('auth_target_path');
  // Only attempt redirect once user has landed on his actual homepage through <Route path="/"> in AppRoutes
  if (targetPath && location.pathname !== '/') {
    localStorage.removeItem('auth_target_path');
    navigate(targetPath);
  }

  return <>{children}</>;
}
